class VariantOptionScroller extends HTMLElement {
  static #VARIANT_SELECTED = 'variant:selected';
  static #VARIANT_UPDATE = 'variant:update';
  static #SCROLL_EDGE_THRESHOLD = 4;

  connectedCallback() {
    this.#scrollShell = this.closest('.variant-option__scroll-shell');
    this.#scrollRow = this.closest('.variant-option__scroll-row');
    this.#prevButton =
      this.#scrollShell?.querySelector('[data-scroll="prev"]') ??
      this.#scrollRow?.querySelector('[data-scroll="prev"]') ??
      this.querySelector('[data-scroll="prev"]');
    this.#nextButton =
      this.#scrollShell?.querySelector('[data-scroll="next"]') ??
      this.#scrollRow?.querySelector('[data-scroll="next"]') ??
      this.querySelector('[data-scroll="next"]');
    this.#overflowList = this.querySelector('overflow-list');
    this.#variantPicker = this.closest('variant-picker');

    this.#prevButton?.addEventListener('click', () => this.#scroll(-1));
    this.#nextButton?.addEventListener('click', () => this.#scroll(1));

    window.addEventListener('resize', this.#handleResize);

    this.addEventListener('change', this.#handleOptionChange);
    this.#variantPicker?.addEventListener(VariantOptionScroller.#VARIANT_SELECTED, this.#handleVariantPickerUpdate);
    this.#variantPicker?.addEventListener(VariantOptionScroller.#VARIANT_UPDATE, this.#handleVariantPickerUpdate);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#bindScrollElement();
      this.#updateArrows();
    });
    this.#resizeObserver.observe(this.#scrollShell ?? this.#scrollRow ?? this);

    this.#scheduleSetup();
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('change', this.#handleOptionChange);
    this.#variantPicker?.removeEventListener(VariantOptionScroller.#VARIANT_SELECTED, this.#handleVariantPickerUpdate);
    this.#variantPicker?.removeEventListener(VariantOptionScroller.#VARIANT_UPDATE, this.#handleVariantPickerUpdate);
    this.#unbindScrollElement();
  }

  /** @type {HTMLButtonElement | null} */
  #prevButton = null;

  /** @type {HTMLButtonElement | null} */
  #nextButton = null;

  /** @type {HTMLElement | null} */
  #overflowList = null;

  /** @type {HTMLElement | null} */
  #scrollList = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {HTMLElement | null} */
  #variantPicker = null;

  /** @type {HTMLElement | null} */
  #scrollRow = null;

  /** @type {HTMLElement | null} */
  #scrollShell = null;

  #handleResize = () => {
    this.#bindScrollElement();
    this.#updateArrows();
  };

  #handleVariantPickerUpdate = () => {
    this.#scheduleSetup(true);
  };

  /** @param {Event} event */
  #handleOptionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
    requestAnimationFrame(() => {
      this.#scrollInputIntoView(event.target, true);
      this.#updateArrows();
    });
  };

  /** @param {boolean} [scrollToSelection] */
  #scheduleSetup(scrollToSelection = true) {
    const run = () => {
      this.#bindScrollElement();
      this.#updateArrows();
      if (scrollToSelection) {
        this.#scrollCheckedIntoView(false);
      }
    };

    requestAnimationFrame(run);
    setTimeout(run, 50);
    setTimeout(run, 200);
  }

  #getScrollElement() {
    const overflowList = this.#overflowList;
    if (!overflowList?.shadowRoot) return null;
    return overflowList.shadowRoot.querySelector('[part="list"]');
  }

  #bindScrollElement() {
    const list = this.#getScrollElement();
    if (!list || list === this.#scrollList) return;

    this.#unbindScrollElement();
    this.#scrollList = list;
    this.#scrollList.addEventListener('scroll', this.#onListScroll, { passive: true });

    if ('onscrollend' in this.#scrollList) {
      this.#scrollList.addEventListener('scrollend', this.#onListScroll);
    }
  }

  #unbindScrollElement() {
    if (!this.#scrollList) return;
    this.#scrollList.removeEventListener('scroll', this.#onListScroll);
    this.#scrollList.removeEventListener('scrollend', this.#onListScroll);
    this.#scrollList = null;
  }

  #onListScroll = () => {
    requestAnimationFrame(() => this.#updateArrows());
  };

  /**
   * @param {HTMLElement} input
   * @param {boolean} [smooth]
   */
  #scrollInputIntoView(input, smooth = true) {
    const list = this.#getScrollElement();
    if (!list) return;

    const item = input.closest('li') ?? input.closest('label');
    if (!item) return;

    this.#scrollElementIntoView(list, item, smooth);
    requestAnimationFrame(() => this.#updateArrows());
    setTimeout(() => this.#updateArrows(), smooth ? 350 : 0);
  }

  /**
   * @param {boolean} [smooth]
   */
  #scrollCheckedIntoView(smooth = true) {
    const list = this.#getScrollElement();
    if (!list) return;

    const checked = this.querySelector('input[type="radio"]:checked');
    if (!checked) return;

    const item = checked.closest('li') ?? checked.closest('label');
    if (!item) return;

    this.#scrollElementIntoView(list, item, smooth);
  }

  /**
   * @param {HTMLElement} list
   * @param {HTMLElement} item
   * @param {boolean} smooth
   */
  #scrollElementIntoView(list, item, smooth) {
    const padding = VariantOptionScroller.#SCROLL_EDGE_THRESHOLD;
    const itemLeft = item.offsetLeft;
    const itemRight = itemLeft + item.offsetWidth;
    const scrollLeft = list.scrollLeft;
    const viewRight = scrollLeft + list.clientWidth;
    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);

    let targetScroll = scrollLeft;

    if (itemLeft < scrollLeft + padding) {
      targetScroll = Math.max(0, itemLeft - padding);
    } else if (itemRight > viewRight - padding) {
      targetScroll = Math.min(maxScroll, itemRight - list.clientWidth + padding);
    } else {
      return;
    }

    list.scrollTo({
      left: targetScroll,
      behavior: smooth ? 'smooth' : 'instant',
    });
  }

  /**
   * @param {number} direction
   */
  #scroll(direction) {
    const list = this.#getScrollElement();
    if (!list) return;

    const amount = Math.max(list.clientWidth * 0.6, 100);
    list.scrollBy({ left: direction * amount, behavior: 'smooth' });

    const updateAfterScroll = () => this.#updateArrows();
    if ('onscrollend' in list) {
      list.addEventListener('scrollend', updateAfterScroll, { once: true });
    }
    setTimeout(updateAfterScroll, 400);
  }

  /** Re-check overflow after quick-add morph or layout changes */
  refresh() {
    this.#scheduleSetup(true);
  }

  #updateArrows() {
    if (!this.#prevButton || !this.#nextButton) return;

    const list = this.#scrollList ?? this.#getScrollElement();
    if (!list) {
      this.classList.remove('variant-option-scroller--scrollable');
      this.#scrollShell?.classList.remove('variant-option-scroller--scrollable');
      this.#scrollRow?.classList.remove('variant-option-scroller--scrollable');
      this.#setScrollRowPosition(null);
      this.#prevButton.hidden = true;
      this.#nextButton.hidden = true;
      return;
    }

    if (!this.#scrollList) {
      this.#bindScrollElement();
    }

    const itemCount = list.children.length;
    const maxScroll = Math.max(0, Math.ceil(list.scrollWidth - list.clientWidth));
    const scrollLeft = Math.round(list.scrollLeft);
    const atStart = scrollLeft <= VariantOptionScroller.#SCROLL_EDGE_THRESHOLD;
    const atEnd = scrollLeft >= maxScroll - VariantOptionScroller.#SCROLL_EDGE_THRESHOLD;
    const scrollable = itemCount > 1 && list.clientWidth > 0 && maxScroll > VariantOptionScroller.#SCROLL_EDGE_THRESHOLD;

    this.classList.toggle('variant-option-scroller--scrollable', scrollable);
    this.#scrollShell?.classList.toggle('variant-option-scroller--scrollable', scrollable);
    this.#scrollRow?.classList.toggle('variant-option-scroller--scrollable', scrollable);

    if (!scrollable) {
      this.#setScrollRowPosition(null);
      this.#prevButton.hidden = true;
      this.#nextButton.hidden = true;
      this.#prevButton.disabled = true;
      this.#nextButton.disabled = true;
      return;
    }

    if (atStart) {
      this.#setScrollRowPosition('start');
      this.#prevButton.hidden = true;
      this.#nextButton.hidden = false;
      this.#prevButton.disabled = true;
      this.#nextButton.disabled = false;
    } else if (atEnd) {
      this.#setScrollRowPosition('end');
      this.#prevButton.hidden = false;
      this.#nextButton.hidden = true;
      this.#prevButton.disabled = false;
      this.#nextButton.disabled = true;
    } else {
      this.#setScrollRowPosition('middle');
      this.#prevButton.hidden = false;
      this.#nextButton.hidden = false;
      this.#prevButton.disabled = false;
      this.#nextButton.disabled = false;
    }
  }

  /**
   * @param {'start' | 'middle' | 'end' | null} position
   */
  #setScrollRowPosition(position) {
    const target = this.#scrollShell ?? this.#scrollRow;
    if (!target) return;

    target.classList.remove(
      'variant-option__scroll-row--at-start',
      'variant-option__scroll-row--at-middle',
      'variant-option__scroll-row--at-end'
    );

    if (position === 'start') {
      target.classList.add('variant-option__scroll-row--at-start');
    } else if (position === 'middle') {
      target.classList.add('variant-option__scroll-row--at-middle');
    } else if (position === 'end') {
      target.classList.add('variant-option__scroll-row--at-end');
    }
  }
}

if (!customElements.get('variant-option-scroller')) {
  customElements.define('variant-option-scroller', VariantOptionScroller);
}
